import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterPanel = ({ onFiltersChange, onDateRangeChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: 'last7days',
    vehicles: [],
    drivers: [],
    routes: [],
    status: 'all',
    priority: 'all'
  });

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const vehicleOptions = [
    { value: 'FL-001', label: 'FL-001 - Heavy Duty Truck' },
    { value: 'FL-002', label: 'FL-002 - Medium Duty Truck' },
    { value: 'FL-003', label: 'FL-003 - Light Duty Truck' },
    { value: 'FL-004', label: 'FL-004 - Heavy Duty Truck' },
    { value: 'FL-005', label: 'FL-005 - Medium Duty Truck' },
    { value: 'FL-006', label: 'FL-006 - Light Duty Truck' }
  ];

  const driverOptions = [
    { value: 'michael-johnson', label: 'Michael Johnson' },
    { value: 'sarah-williams', label: 'Sarah Williams' },
    { value: 'david-chen', label: 'David Chen' },
    { value: 'lisa-rodriguez', label: 'Lisa Rodriguez' },
    { value: 'james-wilson', label: 'James Wilson' },
    { value: 'emma-davis', label: 'Emma Davis' }
  ];

  const routeOptions = [
    { value: 'route-a1', label: 'Route A-1 - Downtown Circuit' },
    { value: 'route-b2', label: 'Route B-2 - Warehouse District' },
    { value: 'route-c3', label: 'Route C-3 - Highway Express' },
    { value: 'route-d4', label: 'Route D-4 - Suburban Loop' },
    { value: 'route-e5', label: 'Route E-5 - Industrial Zone' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'idle', label: 'Idle' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'offline', label: 'Offline' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
    
    if (key === 'dateRange') {
      onDateRangeChange?.(value);
    }
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      dateRange: 'last7days',
      vehicles: [],
      drivers: [],
      routes: [],
      status: 'all',
      priority: 'all'
    };
    setFilters(clearedFilters);
    onFiltersChange?.(clearedFilters);
    onDateRangeChange?.('last7days');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.vehicles?.length > 0) count++;
    if (filters?.drivers?.length > 0) count++;
    if (filters?.routes?.length > 0) count++;
    if (filters?.status !== 'all') count++;
    if (filters?.priority !== 'all') count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-foreground">Filters & Controls</h3>
          {activeFilterCount > 0 && (
            <span className="flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-primary rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            disabled={activeFilterCount === 0}
            iconName="X"
            iconSize={14}
          >
            Clear All
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconSize={16}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </div>
      {/* Quick Filters - Always Visible */}
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Date Range */}
          <Select
            label="Date Range"
            options={dateRangeOptions}
            value={filters?.dateRange}
            onChange={(value) => handleFilterChange('dateRange', value)}
            className="w-full"
          />

          {/* Vehicle Status */}
          <Select
            label="Vehicle Status"
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
            className="w-full"
          />

          {/* Priority */}
          <Select
            label="Priority Level"
            options={priorityOptions}
            value={filters?.priority}
            onChange={(value) => handleFilterChange('priority', value)}
            className="w-full"
          />
        </div>

        {/* Custom Date Range - Show when custom is selected */}
        {filters?.dateRange === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
            <Input
              label="Start Date"
              type="date"
              className="w-full"
            />
            <Input
              label="End Date"
              type="date"
              className="w-full"
            />
          </div>
        )}
      </div>
      {/* Advanced Filters - Expandable */}
      {isExpanded && (
        <div className="p-4 border-t border-border bg-muted/50">
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">Advanced Filters</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Vehicle Selection */}
              <Select
                label="Select Vehicles"
                options={vehicleOptions}
                value={filters?.vehicles}
                onChange={(value) => handleFilterChange('vehicles', value)}
                multiple
                searchable
                clearable
                placeholder="Choose vehicles..."
                className="w-full"
              />

              {/* Driver Selection */}
              <Select
                label="Select Drivers"
                options={driverOptions}
                value={filters?.drivers}
                onChange={(value) => handleFilterChange('drivers', value)}
                multiple
                searchable
                clearable
                placeholder="Choose drivers..."
                className="w-full"
              />

              {/* Route Selection */}
              <Select
                label="Select Routes"
                options={routeOptions}
                value={filters?.routes}
                onChange={(value) => handleFilterChange('routes', value)}
                multiple
                searchable
                clearable
                placeholder="Choose routes..."
                className="w-full"
              />
            </div>

            {/* Filter Summary */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                {filters?.vehicles?.length > 0 && (
                  <span>{filters?.vehicles?.length} vehicle{filters?.vehicles?.length !== 1 ? 's' : ''} selected</span>
                )}
                {filters?.drivers?.length > 0 && (
                  <span>{filters?.drivers?.length} driver{filters?.drivers?.length !== 1 ? 's' : ''} selected</span>
                )}
                {filters?.routes?.length > 0 && (
                  <span>{filters?.routes?.length} route{filters?.routes?.length !== 1 ? 's' : ''} selected</span>
                )}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconSize={14}
              >
                Export Filtered Data
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Quick Actions */}
      <div className="flex items-center justify-between p-4 bg-muted border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="status-indicator status-online"></div>
          <span className="text-sm text-muted-foreground">Live data • Updated 2 min ago</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            iconName="RefreshCw"
            iconSize={14}
          >
            Refresh
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            iconName="Settings"
            iconSize={14}
          >
            Configure
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;